import { logger } from '@curology/logger/client';
import { AxiosError } from 'axios';
import { useState } from 'react';

import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { useSessionExpired } from '@/hooks/useSessionExpired';
import {
  destroyVisitPhoto,
  storeVisitPhoto,
} from '@/services/visitsService/client';
import { useVisitContext } from '@/services/visitsService/hooks/useVisitContext';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';
import { objectToFormData } from '@/utils/http/formData';
import { heicConverter } from '@/utils/images/heic';
import { HEIC_MIME_TYPES } from '@/utils/images/types';

const HEIC_CONVERSION_ERROR_MESSAGE =
  'We were unable to convert your photo. Please try again with a JPEG or PNG image.';

const MAX_FILE_SIZE_MB = 20;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const FILE_SIZE_ERROR_MESSAGE = `File size exceeds ${MAX_FILE_SIZE_MB} MB. Please upload a smaller file.`;

const getErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    return (
      error.response?.data?.message ??
      'An unexpected error occurred while uploading the photo.'
    );
  }
  return 'An unexpected error occurred while uploading the photo.';
};

const getAxiosStatus = (error: unknown): number | undefined =>
  error instanceof AxiosError ? error.response?.status : undefined;

const handlePhotoUploadError = (
  error: unknown,
  handleIfSessionExpired: (status: number | undefined) => boolean,
  onError: ((message: string) => void) | undefined,
): void => {
  if (!handleIfSessionExpired(getAxiosStatus(error))) {
    onError?.(getErrorMessage(error));
  }
};

const getFileForUpload = async (
  file: File,
  perspective: string,
  onError: (message: string) => void,
): Promise<File | null> => {
  if (!HEIC_MIME_TYPES.includes(file.type)) {
    return file;
  }
  try {
    return await heicConverter(file);
  } catch (error) {
    logger.error(error, {
      message: 'Visit photo HEIC conversion failed',
      perspective,
      fileName: file.name,
    });
    onError(HEIC_CONVERSION_ERROR_MESSAGE);
    return null;
  }
};

const mergeVisitWithPhotos = (
  current: { photos?: unknown[] },
  updated: { photos?: unknown[] },
) => ({
  ...current,
  ...updated,
  photos: updated.photos ?? current.photos ?? [],
});

export const usePhotoUpload = (perspective: string) => {
  const [processing, setProcessing] = useState(false);
  const { brand } = useFlowContext();
  const { visit, setVisit } = useVisitContext();
  const { handleIfSessionExpired } = useSessionExpired(brand);

  if (!visit) {
    throw new Error('Visit is not defined');
  }

  const storePhoto = async ({
    files,
    onSuccess,
    onError,
  }: {
    files: File[];
    onSuccess: () => void;
    onError: (message: string) => void;
  }) => {
    if (files.length === 0) {
      return;
    }

    if (files[0].size > MAX_FILE_SIZE_BYTES) {
      onError(FILE_SIZE_ERROR_MESSAGE);
      return;
    }

    setProcessing(true);

    const file = await getFileForUpload(files[0], perspective, onError);
    if (!file) {
      setProcessing(false);
      return;
    }

    const data = objectToFormData({
      file,
      metadata: {
        photo_perspective: perspective,
      },
    });

    try {
      const updatedVisit = await storeVisitPhoto({
        uuid: visit.uuid,
        brand,
        data,
      });

      onSuccess();
      setVisit(mergeVisitWithPhotos(visit, updatedVisit) as typeof visit);

      trackEvent(AnalyticsEvent.PHOTO_UPLOADED, {
        type: perspective,
      });
    } catch (error) {
      handlePhotoUploadError(error, handleIfSessionExpired, onError);
    } finally {
      setProcessing(false);
    }
  };

  const deletePhoto = async ({
    id,
    onError,
  }: {
    id: number;
    onError?: (message: string) => void;
  }) => {
    setProcessing(true);
    try {
      const updatedVisit = await destroyVisitPhoto({
        brand,
        photoId: id,
        uuid: visit.uuid,
      });
      setVisit(mergeVisitWithPhotos(visit, updatedVisit) as typeof visit);

      trackEvent(AnalyticsEvent.PHOTO_REMOVED, {
        type: perspective,
      });
    } catch (error) {
      handlePhotoUploadError(error, handleIfSessionExpired, onError);
    } finally {
      setProcessing(false);
    }
  };

  return {
    processing,
    storePhoto,
    deletePhoto,
  };
};
